import type { Lesson } from '../schema.js';

// Hearts lessons: explain + quiz only. The facts match the game as built (packages/game-engine/src/hearts/play.ts):
// four players, each heart is 1 point and the queen of spades is 13 (26 in all), the goal is to take as FEW points
// as you can, every hand starts by passing three cards (left, right, across, then no pass, round and round), the
// two of clubs leads the first trick and no points may be played on that trick unless you hold nothing else,
// hearts cannot be led until one has been played, taking every point ("shooting the moon") scores 0 for you and 26
// for everyone else, and a game ends when someone reaches 100, when the LOWEST total wins. These rules are
// recalled, not checked against a rulebook, and a player should confirm them.
export const HEARTS_LESSONS: Lesson[] = [
  {
    id: 'hearts-points',
    slug: 'hearts-points-and-passing',
    title: 'Hearts: avoid the points',
    audienceLevel: 'beginner',
    concepts: ['hearts'],
    steps: [
      {
        kind: 'explain',
        title: 'Take as few points as you can',
        body: 'Hearts is the opposite of most card games: you do not want to win points. Every heart you take in a trick costs 1 point, and the queen of spades costs 13, so there are 26 bad points in every hand. Before each hand you pass three cards to another player, so you can get rid of cards you do not want. You pass to your left on the first hand, to your right on the second, across the table on the third, and on the fourth hand nobody passes. Then it starts over.',
        narration: 'Hearts cost one point each and the queen of spades costs thirteen. Pass away the cards you do not want.',
      },
      {
        kind: 'quiz',
        title: 'Points and passing',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'Which card costs the most points?',
            choices: ['The queen of spades', 'The ace of hearts', 'The king of clubs'],
            correct: 0,
          },
          {
            prompt: 'You take a trick with three hearts and the queen of spades in it. How many points is that?',
            choices: ['16', '4', '13'],
            correct: 0,
          },
          {
            prompt: 'How many cards do you pass before a hand, when there is a pass?',
            choices: ['Three', 'One', 'Five'],
            correct: 0,
          },
        ],
      },
    ],
  },
  {
    id: 'hearts-play',
    slug: 'hearts-playing-and-winning',
    title: 'Hearts: playing the hand',
    audienceLevel: 'beginner',
    concepts: ['hearts'],
    steps: [
      {
        kind: 'explain',
        title: 'Rules of the tricks',
        body: 'The player with the two of clubs leads the first trick with it. Everyone must follow the suit that was led if they can, and the highest card of that suit takes the trick and leads next. Nobody may play a heart or the queen of spades on the very first trick, unless that is all they hold. You cannot lead a heart until a heart has been played in an earlier trick, unless you hold nothing but hearts. If one player takes every heart and the queen of spades, that is called shooting the moon: that player adds 0 points and everybody else adds 26. A game ends when someone reaches 100 points, and the player with the lowest total wins.',
        narration: 'Follow suit, avoid hearts, and the lowest score at one hundred wins.',
      },
      {
        kind: 'quiz',
        title: 'Playing the hand',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'Which card leads the very first trick?',
            choices: ['The two of clubs', 'The ace of spades', 'Any card'],
            correct: 0,
          },
          {
            prompt: 'You take every heart and the queen of spades in one hand. How many points do you add to your total?',
            choices: ['0', '26', '13'],
            correct: 0,
          },
          {
            prompt: 'The game ends at 100 points. Who wins?',
            choices: ['The player with the lowest total', 'The player with the highest total', 'The first player to reach 100'],
            correct: 0,
          },
        ],
      },
    ],
  },
];
